import mustache from "mustache";
import { IErrorService } from "../../abstractions/services/IErrorService";
import template from "./errorTemplate.html";

mustache.parse(template);

/** Notificación (toast) en cola: tipo, textos e id para removerla. */
type ErrorItem = {
  /** Id incremental (clave para el cierre/auto-remove). */
  id: number;
  /** Toast de éxito (verde). */
  isSuccess: boolean;
  /** Toast de error (rojo). */
  isDanger: boolean;
  /** Toast de advertencia (amarillo). */
  isWarning: boolean;
  /** Título del toast. */
  title: string;
  /** Descripción del toast. */
  description: string;
};

/** Servicio de notificaciones (toasts) de la app: renderiza una cola de avisos
 *  (éxito/error/advertencia) en un contenedor fijo, deduplica repetidos y los
 *  auto-remueve a los 30s. */
class ErrorService implements IErrorService {
  /** Cola de notificaciones visibles. */
  private _items: ErrorItem[] = [];
  /** Id incremental para la próxima notificación. */
  private _nextId = 0;
  /** Contenedor raíz de los toasts (creado en `init`). */
  private _element!: HTMLDivElement;
  /** Guard para no enganchar el listener de cierre más de una vez. */
  private _listenerAttached = false;

  /** Crea y monta el contenedor de toasts en `document.body`. */
  init(): void {
    this._element = document.createElement("div");
    this._element.id = "errorWrapper";
    document.body.appendChild(this._element);
  }

  /** Muestra un toast de error (rojo). */
  addError(title: string, message: string): void {
    this._add(false, true, false, title, message);
  }

  /** Muestra un toast de éxito (verde). */
  addSuccess(title: string, message: string): void {
    this._add(true, false, false, title, message);
  }

  /** Muestra un toast de advertencia (amarillo). */
  addWarning(title: string, message: string): void {
    this._add(false, false, true, title, message);
  }

  /** Encola un toast (deduplica por título+descripción) y programa su auto-remove a los 30s. */
  private _add(
    isSuccess: boolean,
    isDanger: boolean,
    isWarning: boolean,
    title: string,
    description: string,
  ): void {
    this._attachCloseListener();

    if (
      this._items.some(
        (i) => i.title === title && i.description === description,
      )
    )
      return;

    const item: ErrorItem = {
      id: this._nextId++,
      description,
      isDanger,
      isSuccess,
      isWarning,
      title,
    };
    this._items.push(item);
    this._render();
    setTimeout(() => this._remove(item.id), 30_000);
  }

  /** Quita un toast por id y re-renderiza. */
  private _remove(id: number): void {
    this._items = this._items.filter((r) => r.id !== id);
    this._render();
  }

  /** Re-renderiza la cola de toasts en el contenedor. */
  private _render(): void {
    this._element.innerHTML = mustache.render(template, { items: this._items });
  }

  /** Engancha (una sola vez) el listener delegado de cierre por `[data-id]`. */
  private _attachCloseListener(): void {
    if (this._listenerAttached) return;
    this._listenerAttached = true;
    this._element.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest("[data-id]");
      if (btn) this._remove(Number(btn.getAttribute("data-id")));
    });
  }
}

/** Singleton del servicio de notificaciones. */
export const errorService = new ErrorService();
